<template>
  <div class="honors-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextHtml && richtextHtml.trim()"
            class="richtext-content"
            v-html="richtextHtml"
          ></div>

          <!-- 荣誉图片轮播 -->
          <div v-if="imageList && imageList.length > 0" class="honors-carousel-section">
            <h2 class="carousel-title">{{ t('group_structure.certificates') }}</h2>

            <!-- 轮播图容器 -->
            <div class="carousel-container">
              <div class="carousel-wrapper">
                <!-- 轮播图主区域 -->
                <div class="carousel-main">
                  <div
                    class="carousel-track"
                    :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
                  >
                    <div
                      v-for="(image, index) in imageList"
                      :key="index"
                      class="carousel-slide"
                    >
                      <div class="slide-content">
                        <div class="image-container">
                          <img
                            :src="image.path"
                            :alt="image.name"
                            class="honor-image"
                            @error="handleImageError"
                            @click="openImagePreview(index)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 导航箭头 -->
                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow prev-arrow"
                    @click="prevSlide"
                    :disabled="currentIndex === 0"
                  >
                    <el-icon size="24">
                      <ArrowLeft />
                    </el-icon>
                  </button>

                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow next-arrow"
                    @click="nextSlide"
                    :disabled="currentIndex === imageList.length - 1"
                  >
                    <el-icon size="24">
                      <ArrowRight />
                    </el-icon>
                  </button>

                  <!-- 图片名称展示 -->
                  <div v-if="imageList[currentIndex]?.name" class="image-name">
                    {{ imageList[currentIndex].name }}
                  </div>
                </div>

                <!-- 缩略图导航 -->
                <div v-if="imageList.length > 1" class="thumbnail-nav">
                  <div
                    v-for="(image, index) in imageList"
                    :key="index"
                    class="thumbnail-item"
                    :class="{ active: currentIndex === index }"
                    @click="goToSlide(index)"
                  >
                    <div class="thumbnail-container">
                      <img
                        :src="image.path"
                        :alt="image.name"
                        class="thumbnail-image"
                      />
                      <div class="thumbnail-overlay"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态（当没有任何内容时显示） -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <Trophy />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('group_structure.no_honors') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      v-model="previewVisible"
      :show-close="true"
      width="90vw"
      :destroy-on-close="true"
      class="image-preview-dialog"
      @close="closePreview"
    >
      <div class="preview-container">
        <!-- 图片预览区域：支持鼠标拖拽和触摸拖拽 -->
        <div
          class="preview-image-wrapper"
          ref="previewWrapper"
          @wheel.prevent="handleWheel"
          @mousedown.left="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @touchstart="startTouchDrag"
          @touchmove="onTouchDrag"
          @touchend="stopDrag"
          @touchcancel="stopDrag"
        >
          <img
            ref="previewImage"
            :src="previewImageSrc"
            :alt="previewImageAlt"
            class="preview-image"
            :style="{
              transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${scale}) rotate(${rotate}deg)`,
              cursor: isDragging ? 'grabbing' : 'grab'
            }"
            @load="onPreviewImageLoad"
          />
        </div>

        <!-- 控制按钮 -->
        <div class="preview-controls">
          <button class="control-btn" @click="zoomIn" title="放大">
            <el-icon size="20"><ZoomIn /></el-icon>
          </button>
          <button class="control-btn" @click="zoomOut" title="缩小">
            <el-icon size="20"><ZoomOut /></el-icon>
          </button>
          <button class="control-btn" @click="rotateImage" title="旋转">
            <el-icon size="20"><RefreshRight /></el-icon>
          </button>
          <button class="control-btn" @click="resetPreview" title="重置">
            <el-icon size="20"><RefreshLeft /></el-icon>
          </button>
          <span class="scale-info">{{ Math.round(scale * 100) }}%</span>
        </div>

        <!-- 导航箭头 -->
        <button
          v-if="imageList.length > 1"
          class="preview-arrow prev-arrow"
          @click="prevPreviewImage"
        >
          <el-icon size="28">
            <ArrowLeft />
          </el-icon>
        </button>
        <button
          v-if="imageList.length > 1"
          class="preview-arrow next-arrow"
          @click="nextPreviewImage"
        >
          <el-icon size="28">
            <ArrowRight />
          </el-icon>
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, ArrowLeft, ArrowRight, Trophy, ZoomIn, ZoomOut, RefreshRight, RefreshLeft, Picture } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const currentIndex = ref(0)
const autoPlayTimer = ref(null)
const isAutoPlaying = ref(true)

// 预览弹窗相关
const previewVisible = ref(false)
const previewImageSrc = ref('')
const previewImageAlt = ref('')
const previewIndex = ref(0)
const scale = ref(1)
const rotate = ref(0)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const dragOffset = ref({ x: 0, y: 0 })
const previewWrapper = ref(null)
const previewImage = ref(null)
const imageDisplayWidth = ref(0)
const imageDisplayHeight = ref(0)

// 移动端长按相关
const touchLongPressTimer = ref(null)
const isTouchLongPress = ref(false)

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.group_structure')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

const imageList = computed(() => {
  return pageData.value?.content?.image_list || []
})

// 轮播图方法
const nextSlide = () => {
  if (currentIndex.value < imageList.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0 // 循环到第一张
  }
  resetAutoPlay()
}

const prevSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = imageList.value.length - 1 // 循环到最后一张
  }
  resetAutoPlay()
}

const goToSlide = (index) => {
  currentIndex.value = index
  resetAutoPlay()
}

// 自动播放
const startAutoPlay = () => {
  if (imageList.value.length <= 1) return

  clearInterval(autoPlayTimer.value)
  autoPlayTimer.value = setInterval(() => {
    if (isAutoPlaying.value) {
      nextSlide()
    }
  }, 5000) // 5秒自动切换
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  clearInterval(autoPlayTimer.value)
}

const resetAutoPlay = () => {
  stopAutoPlay()
  setTimeout(() => {
    isAutoPlaying.value = true
    startAutoPlay()
  }, 100)
}

// 图片加载失败处理
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const placeholder = document.createElement('div')
  placeholder.className = 'image-error-placeholder'
  placeholder.innerHTML = `
    <div class="placeholder-icon">
      <el-icon size="40" color="var(--el-color-primary-light-6)">
        <Picture />
      </el-icon>
    </div>
    <p class="placeholder-text">图片加载失败</p>
  `
  event.target.parentElement.appendChild(placeholder)
}

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/group_structure'
    })

    if (response && response.content) {
      pageData.value = response

      // 如果有图片，启动自动播放
      if (imageList.value.length > 0) {
        nextTick(() => {
          startAutoPlay()
        })
      }
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  stopAutoPlay()
  clearInterval(autoPlayTimer.value)
  clearTimeout(touchLongPressTimer.value)
})

// 图片预览方法
const openImagePreview = (index) => {
  previewIndex.value = index
  previewImageSrc.value = imageList.value[index].path
  previewImageAlt.value = imageList.value[index].name
  previewVisible.value = true
  resetPreviewState()
}

const closePreview = () => {
  previewVisible.value = false
  resetPreviewState()
}

const resetPreviewState = () => {
  scale.value = 1
  rotate.value = 0
  dragOffset.value = { x: 0, y: 0 }
  isDragging.value = false
  isTouchLongPress.value = false
  clearTimeout(touchLongPressTimer.value)
}

const resetPreview = () => {
  resetPreviewState()
}

const zoomIn = () => {
  if (scale.value < 5) {
    scale.value = Math.min(5, scale.value + 0.25)
  }
}

const zoomOut = () => {
  if (scale.value > 0.25) {
    scale.value = Math.max(0.25, scale.value - 0.25)
  }
}

const zoomWithFocus = (delta, focusX, focusY) => {
  const oldScale = scale.value
  const newScale = Math.max(0.25, Math.min(5, oldScale + delta))
  if (newScale === oldScale) return

  const wrapperRect = previewWrapper.value?.getBoundingClientRect()
  if (!wrapperRect) return

  const wrapperCenterX = wrapperRect.width / 2
  const wrapperCenterY = wrapperRect.height / 2

  const focusImgX = (focusX - wrapperCenterX - dragOffset.value.x) / oldScale
  const focusImgY = (focusY - wrapperCenterY - dragOffset.value.y) / oldScale

  const newOffsetX = focusX - wrapperCenterX - focusImgX * newScale
  const newOffsetY = focusY - wrapperCenterY - focusImgY * newScale

  scale.value = newScale
  dragOffset.value = {
    x: newOffsetX,
    y: newOffsetY
  }
  clampOffset()
}

const clampOffset = () => {
  if (!previewWrapper.value || imageDisplayWidth.value === 0 || imageDisplayHeight.value === 0) return

  const wrapperRect = previewWrapper.value.getBoundingClientRect()
  const wrapperWidth = wrapperRect.width
  const wrapperHeight = wrapperRect.height

  const scaledWidth = imageDisplayWidth.value * scale.value
  const scaledHeight = imageDisplayHeight.value * scale.value

  const maxOffsetX = Math.max(0, (scaledWidth - wrapperWidth) / 2)
  const maxOffsetY = Math.max(0, (scaledHeight - wrapperHeight) / 2)

  dragOffset.value = {
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, dragOffset.value.x)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, dragOffset.value.y))
  }
}

const rotateImage = () => {
  rotate.value = (rotate.value + 90) % 360
}

const handleWheel = (event) => {
  event.preventDefault()
  const wrapperRect = previewWrapper.value?.getBoundingClientRect()
  if (!wrapperRect) return

  const focusX = event.clientX - wrapperRect.left
  const focusY = event.clientY - wrapperRect.top

  const delta = event.deltaY > 0 ? -0.25 : 0.25
  zoomWithFocus(delta, focusX, focusY)
}

// ==================== 改进的拖拽逻辑 ====================

/**
 * 桌面端：鼠标按下开始拖拽
 * 点击后按住鼠标移动图片，松开即固定位置
 */
const startDrag = (event) => {
  // 防止拖拽时选中文本
  event.preventDefault()

  isDragging.value = true
  // 记录初始位置：当前鼠标位置 - 当前偏移量
  dragStart.value = {
    x: event.clientX - dragOffset.value.x,
    y: event.clientY - dragOffset.value.y
  }

  // 修改鼠标样式为抓取状态
  if (previewWrapper.value) {
    previewWrapper.value.style.cursor = 'grabbing'
  }
}

/**
 * 桌面端：鼠标移动时更新图片位置
 * 只在按住鼠标时有效
 */
const onDrag = (event) => {
  if (!isDragging.value) return

  event.preventDefault()

  // 计算新的偏移量：当前鼠标位置 - 初始记录位置
  dragOffset.value = {
    x: event.clientX - dragStart.value.x,
    y: event.clientY - dragStart.value.y
  }

  // 限制拖拽范围，防止图片拖出容器太远
  clampOffset()
}

/**
 * 桌面端和移动端：鼠标/触摸松开，停止拖拽
 * 图片位置固定在当前位置
 */
const stopDrag = () => {
  isDragging.value = false
  isTouchLongPress.value = false
  clearTimeout(touchLongPressTimer.value)

  // 恢复鼠标样式为抓取准备状态
  if (previewWrapper.value) {
    previewWrapper.value.style.cursor = 'grab'
  }
}

/**
 * 移动端：触摸开始
 * 启动长按计时器，长按 500ms 后才能拖拽
 */
const startTouchDrag = (event) => {
  if (event.touches.length !== 1) return

  const touch = event.touches[0]

  // 清除之前的长按计时器
  clearTimeout(touchLongPressTimer.value)

  // 启动长按计时器
  touchLongPressTimer.value = setTimeout(() => {
    isTouchLongPress.value = true
    isDragging.value = true

    // 记录初始位置
    dragStart.value = {
      x: touch.clientX - dragOffset.value.x,
      y: touch.clientY - dragOffset.value.y
    }
  }, 500) // 长按 500ms 触发
}

/**
 * 移动端：触摸移动
 * 只在长按激活后才能拖拽
 */
const onTouchDrag = (event) => {
  // 只有长按激活后才能拖拽
  if (!isDragging.value || !isTouchLongPress.value || event.touches.length !== 1) return

  event.preventDefault()

  const touch = event.touches[0]

  // 计算新的偏移量
  dragOffset.value = {
    x: touch.clientX - dragStart.value.x,
    y: touch.clientY - dragStart.value.y
  }

  // 限制拖拽范围
  clampOffset()
}

const prevPreviewImage = () => {
  if (previewIndex.value > 0) {
    previewIndex.value--
  } else {
    previewIndex.value = imageList.value.length - 1
  }
  previewImageSrc.value = imageList.value[previewIndex.value].path
  previewImageAlt.value = imageList.value[previewIndex.value].name
  resetPreviewState()
}

const nextPreviewImage = () => {
  if (previewIndex.value < imageList.value.length - 1) {
    previewIndex.value++
  } else {
    previewIndex.value = 0
  }
  previewImageSrc.value = imageList.value[previewIndex.value].path
  previewImageAlt.value = imageList.value[previewIndex.value].name
  resetPreviewState()
}

const onPreviewImageLoad = () => {
  if (previewImage.value) {
    imageDisplayWidth.value = previewImage.value.clientWidth
    imageDisplayHeight.value = previewImage.value.clientHeight
    // 加载完成后限制一次偏移范围
    clampOffset()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/carousel';

// 预览图片容器样式优化
.preview-image-wrapper {
  touch-action: none; // 禁用浏览器默认的触摸行为
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none; // 禁用iOS长按菜单

  // 确保容器能正确处理事件
  position: relative;
  overflow: hidden;
}

// 预览图片样式
.preview-image {
  transition: cursor 0.2s ease;
  will-change: transform; // 优化性能
}
</style>
